import { Alert, Box, Stack, Typography } from '@mui/material';
import { CircleCheck, CircleX, Lock, ScrollText } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';

import { Button, Dialog, Field, Icon, SelectField } from '@/ui/kit';

import type { SensitiveActionInput } from './adminApi';
import { OTHER_REASON_CODE, type ReasonOption } from './sensitiveActions';

/**
 * Confirmation unique de toutes les actions sensibles de l'administration
 * (maquette P06, ADR 0035 point 3), batie sur le kit (`ui/kit`, BO-01).
 *
 * Une action sensible est une action portant sur les donnees d'un TIERS, sur la
 * disponibilite du service, ou sur quelque chose d'irreversible. Elles partagent
 * un seul dispositif, et c'est deliberé : huit confirmations ecrites huit fois
 * seraient protegees de huit facons, et l'une d'elles le serait mal. Le serveur
 * applique les memes regles (`SensitiveAction`) ; cette boite ne fait que les
 * rendre visibles avant l'appel.
 *
 * Trois sections affichees ensemble, chacune pour une raison precise :
 *
 * 1. **Les consequences, AVANT le geste**, en deux colonnes : ce qui disparait,
 *    ce qui est conserve. « Anonymiser » n'est pas « supprimer » : la
 *    tracabilite des lots survit (ADR 0011), et l'ecran doit le dire, sinon
 *    l'administrateur croit detruire ce qu'il ne detruit pas.
 * 2. **Un motif type**, dans la liste fermee de l'action, et sa precision —
 *    obligatoire pour « Autre motif ». C'est lui qui rend le journal d'audit
 *    exploitable six mois plus tard.
 * 3. **La saisie exacte de l'objet vise**, et pour une action lourde le **mot
 *    de passe de l'operateur**. Une case a cocher ne protege de rien : le clic
 *    reflexe est le mode d'echec reel.
 *
 * Le bouton reste desactive tant que tout n'est pas satisfait, et son libelle
 * nomme l'action reelle — jamais « OK », jamais « Confirmer ».
 *
 * **Le mot de passe ne survit pas a la boite** : il est vide a chaque
 * ouverture, a chaque fermeture et apres chaque refus, et il ne part que dans
 * le corps de l'appel.
 *
 * **Le gestionnaire de mots de passe du navigateur ne doit jamais rattacher
 * ce mot de passe a l'adresse de la famille.** Sans champ `username`, son
 * heuristique prend le dernier champ texte avant le mot de passe — la
 * confirmation, qui porte l'adresse de la personne visee — et propose
 * d'enregistrer le secret de l'operateur sous l'adresse d'un tiers, ou de
 * preremplir la confirmation. D'ou le modele « sudo » : un champ cache
 * `autocomplete="username"` portant l'adresse de l'**operateur**, et
 * `autocomplete="off"` sur la confirmation. `current-password` reste sur le
 * mot de passe (WCAG 2.2, critere 3.3.8 : authentification sans test cognitif).
 *
 * Le composant ne connait aucune route : il rend un `SensitiveActionInput` et
 * laisse l'appelant decider quoi en faire.
 */

/** Ce que l'action fait disparaitre et ce qu'elle conserve, ecrit par l'appelant. */
export interface SensitiveActionConsequence {
  /** Phrase d'introduction facultative, au-dessus des deux colonnes. */
  readonly summary?: ReactNode;
  readonly disappears: readonly ReactNode[];
  readonly kept: readonly ReactNode[];
  /** Titre de la premiere colonne ; « Disparaît définitivement » par defaut. */
  readonly disappearsTitle?: string;
  /** Titre de la seconde colonne ; « Est conservé » par defaut. */
  readonly keptTitle?: string;
}

export interface SensitiveActionDialogProps {
  readonly open: boolean;
  /** Titre court, a l'infinitif : « Anonymiser ce compte ». */
  readonly title: string;
  /** Ligne sous le titre : l'objet vise, et ce qui caracterise le geste. */
  readonly subtitle?: string;
  readonly consequence: SensitiveActionConsequence;
  /** Liste fermee des motifs de l'action, miroir du serveur (`sensitiveActions.ts`). */
  readonly reasonOptions: readonly ReasonOption[];
  /** L'action exige-t-elle le mot de passe de l'operateur ? */
  readonly requiresPassword: boolean;
  /**
   * Adresse du compte d'exploitation connecte. Elle n'est pas affichee : elle
   * dit au gestionnaire de mots de passe a QUI appartient le mot de passe saisi.
   */
  readonly operatorEmail: string;
  /** Valeur que l'utilisateur doit retaper, exactement. */
  readonly expectedConfirmation: string;
  /** Ce que designe cette valeur, pour le libelle du champ. */
  readonly confirmationLabel: string;
  /** Libelle du bouton d'action. Nomme l'action reelle. */
  readonly actionLabel: string;
  /** `true` pendant l'appel : les champs et les boutons se verrouillent. */
  readonly pending?: boolean;
  /** Message d'erreur du dernier essai, affiche dans la boite. */
  readonly error?: string | null;
  /**
   * Code d'erreur du dernier essai (`{error: {code}}`). Un refus du mot de passe
   * (`reauthentication_failed`, `rate_limited`) s'affiche SOUS le champ mot de
   * passe, qui reprend le focus ; tout autre refus, dans l'encadre du corps.
   */
  readonly errorCode?: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (input: SensitiveActionInput) => void;
}

const MAX_REASON = 500;

/** Refus qui portent sur le mot de passe : ils se lisent sous ce champ. */
const PASSWORD_ERROR_CODES: readonly string[] = ['reauthentication_failed', 'rate_limited'];

/** Amene l'element entier dans la vue du corps defilant ; absent sous jsdom. */
const reveal = (element: Element | null): void => {
  if (element !== null && typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ block: 'nearest' });
  }
};

const COLUMN_TONES = {
  disappears: { surface: 'tokens.criticalSoft', ink: 'tokens.criticalText', icon: CircleX },
  kept: { surface: 'tokens.successSoft', ink: 'tokens.successText', icon: CircleCheck },
} as const;

const ConsequenceColumn = ({
  title,
  items,
  tone,
}: {
  title: string;
  items: readonly ReactNode[];
  tone: keyof typeof COLUMN_TONES;
}): ReactElement => {
  const { surface, ink, icon } = COLUMN_TONES[tone];

  return (
    // Un groupe nomme et non une alerte : une consequence ecrite d'avance n'a
    // pas a etre annoncee d'office a l'ouverture. La croix et la coche disent
    // le sens sans compter sur la couleur.
    <Box
      role="group"
      aria-label={title}
      sx={{ flex: 1, minWidth: 0, p: 1.75, borderRadius: '12px', bgcolor: surface }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: ink, mb: 1 }}>{title}</Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((item, index) => (
          <Box
            component="li"
            // Listes ecrites par l'appelant, jamais reordonnees : l'index suffit.
            key={index}
            sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', color: ink }}
          >
            <Box component="span" sx={{ display: 'inline-flex', mt: '2px' }}>
              <Icon icon={icon} size={16} />
            </Box>
            <Typography component="span" sx={{ fontSize: 13, lineHeight: '19px', color: 'tokens.textStrong' }}>
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const SensitiveActionDialog = ({
  open,
  title,
  subtitle,
  consequence,
  reasonOptions,
  requiresPassword,
  operatorEmail,
  expectedConfirmation,
  confirmationLabel,
  actionLabel,
  pending = false,
  error = null,
  errorCode = null,
  onCancel,
  onConfirm,
}: SensitiveActionDialogProps): ReactElement => {
  const [reasonCode, setReasonCode] = useState('');
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [wasOpen, setWasOpen] = useState(open);
  const [lastError, setLastError] = useState(error);
  const passwordId = useId();
  const passwordRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  // Chaque ouverture repart de zero. Sans cela, un motif saisi pour un compte
  // resterait pose sur le suivant, et l'audit enregistrerait une raison qui ne
  // correspond a rien — un journal qui ment est pire qu'un journal vide.
  // La FERMETURE vide aussi le mot de passe : une boite fermee mais montee
  // (les trois boites de la fiche compte le sont) ne doit pas le garder.
  //
  // L'ajustement se fait PENDANT LE RENDU et non dans un `useEffect` : c'est le
  // patron officiel de React pour « remettre un etat a zero quand une propriete
  // change » (react.dev, « You Might Not Need an Effect »). Un effet produirait
  // un premier rendu portant encore les valeurs precedentes, et
  // `react-hooks/set-state-in-effect` le refuse a juste titre.
  if (open !== wasOpen) {
    setWasOpen(open);
    setPassword('');

    if (open) {
      setReasonCode('');
      setReason('');
      setConfirmation('');
    }
  }

  // Un refus vide le mot de passe et garde le reste : on retape le secret, on
  // ne recommence pas tout le motif.
  if (error !== lastError) {
    setLastError(error);

    if (error !== null) {
      setPassword('');
    }
  }

  // Le refus du mot de passe devient l'erreur du champ mot de passe : le
  // message est sous le champ a retaper, relie par `aria-describedby`, et le
  // champ passe `aria-invalid`. Un encadre en bas du corps restait hors de la
  // vue, a cote d'un champ vide et d'un bouton grise, sans explication.
  const passwordError =
    requiresPassword && error !== null && errorCode !== null && PASSWORD_ERROR_CODES.includes(errorCode)
      ? error
      : undefined;

  // A l'arrivee d'un refus : le focus revient au champ mot de passe — le
  // bouton qui l'avait est desormais desactive, et le focus retombait sur le
  // conteneur — et le message est amene dans la vue. Un autre refus amene son
  // encadre dans la vue. Effet et non rendu : il agit sur le DOM monte.
  useEffect(() => {
    if (error === null) {
      return;
    }

    if (passwordError !== undefined) {
      passwordRef.current?.focus();
      reveal(document.getElementById(`${passwordId}-aide`));
    } else {
      reveal(alertRef.current);
    }
  }, [error, passwordError, passwordId]);

  const otherSelected = reasonCode === OTHER_REASON_CODE;
  const trimmedReason = reason.trim();
  const reasonTooLong = trimmedReason.length > MAX_REASON;
  const reasonCodeValid = reasonOptions.some((option) => option.code === reasonCode);
  const reasonValid = !reasonTooLong && (!otherSelected || trimmedReason !== '');
  // Comparaison EXACTE, sans normalisation de casse : c'est la relecture qui est
  // recherchee, pas la saisie approchante.
  const confirmationValid = confirmation === expectedConfirmation;
  const passwordValid = !requiresPassword || password !== '';
  const ready = reasonCodeValid && reasonValid && confirmationValid && passwordValid && !pending;

  const reasonError = reasonTooLong
    ? `Précision trop longue (${String(trimmedReason.length)} / ${String(MAX_REASON)} caractères).`
    : otherSelected && reason !== '' && trimmedReason === ''
      ? 'La précision ne peut pas être faite que d’espaces.'
      : undefined;

  const submit = (): void => {
    onConfirm({
      reasonCode,
      confirmation,
      ...(trimmedReason === '' ? {} : { reason: trimmedReason }),
      ...(requiresPassword ? { password } : {}),
    });
  };

  return (
    <Dialog
      open={open}
      // Absent pendant l'appel : ni Echap, ni voile, ni « × » ne ferment une
      // boite dont l'operation part deja au serveur.
      onClose={pending ? undefined : onCancel}
      title={title}
      description={subtitle}
      actions={
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: '1 1 180px',
              alignSelf: 'center',
              color: 'tokens.textMuted',
            }}
          >
            <Icon icon={ScrollText} size={16} />
            <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              Inscrit au journal d’audit avec votre identité et ce motif.
            </Typography>
          </Box>
          <Button onClick={onCancel} disabled={pending}>
            Annuler
          </Button>
          <Button variant="danger" disabled={!ready} onClick={submit}>
            {pending ? 'En cours…' : actionLabel}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        {consequence.summary === undefined ? null : (
          <Typography component="div" sx={{ fontSize: 14, color: 'tokens.textStrong' }}>
            {consequence.summary}
          </Typography>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {/* Une colonne vide n'est pas rendue : une croix devant « rien »
              se lirait comme une perte. */}
          {consequence.disappears.length === 0 ? null : (
            <ConsequenceColumn
              tone="disappears"
              title={consequence.disappearsTitle ?? 'Disparaît définitivement'}
              items={consequence.disappears}
            />
          )}
          {consequence.kept.length === 0 ? null : (
            <ConsequenceColumn
              tone="kept"
              title={consequence.keptTitle ?? 'Est conservé'}
              items={consequence.kept}
            />
          )}
        </Stack>

        <SelectField
          label="Motif"
          required
          value={reasonCode}
          onChange={setReasonCode}
          disabled={pending}
          hint="Choisi dans la liste de cette action. Il est enregistré au journal d’audit."
          options={reasonOptions.map((option) => ({ value: option.code, label: option.label }))}
        />

        <Field
          label="Motif précisé"
          required={otherSelected}
          hint={`${otherSelected ? 'Obligatoire pour « Autre motif ».' : 'Facultatif.'} Sans donnée de santé ni nom d’enfant : le texte est conservé tel quel au journal d’audit.`}
          error={reasonError}
          value={reason}
          onChange={setReason}
          disabled={pending}
          multiline
          minRows={2}
        />

        <Field
          label={`Saisissez ${confirmationLabel} pour confirmer`}
          required
          hint={expectedConfirmation}
          error={confirmation !== '' && !confirmationValid ? 'La saisie ne correspond pas exactement.' : undefined}
          value={confirmation}
          onChange={setConfirmation}
          disabled={pending}
          // `off` : ce champ n'est pas un identifiant, et le navigateur ne doit
          // ni le preremplir ni y rattacher le mot de passe de l'operateur.
          name="confirmation"
          autoComplete="off"
        />

        {requiresPassword ? (
          <>
            {/* Identifiant du gestionnaire de mots de passe : l'operateur, et
                non la famille. Cache, hors tabulation et hors lecteur d'ecran. */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={operatorEmail}
              readOnly
              hidden
              data-testid="sensitive-action-operator-username"
            />
            <Field
              id={passwordId}
              inputRef={passwordRef}
              label="Votre mot de passe"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              icon={Lock}
              hint="Une session restée ouverte ne suffit pas pour cette action : le serveur vérifie votre mot de passe, sans le conserver."
              error={passwordError}
              value={password}
              onChange={setPassword}
              disabled={pending}
            />
          </>
        ) : null}

        {/* Le refus s'affiche sous les champs, au plus pres de ce qu'il faut
            corriger — le mot de passe, la confirmation —, et juste au-dessus
            du bouton qu'on vient de presser. */}
        {error === null || passwordError !== undefined ? null : (
          <Alert ref={alertRef} severity="error" variant="outlined" role="alert">
            {error}
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
};
